/*
 * Protokoll-Token-Wissen des Frontends — eine Tabelle, nicht drei.
 *
 * Bis hierher lag die Modbus-Familie als eigene Kopie in
 * ApplySchemaTemplateModal.tsx, und das Gate „Modbus hat keine Discovery"
 * gab es an den beiden Stellen, an denen der Discovery-Knopf tatsächlich
 * sitzt, überhaupt nicht. Drei Kopien einer Protokoll-Liste sind genau die
 * Drift, die dieses Projekt schon mehrfach Feldzeit gekostet hat.
 */

/**
 * Protokolle mit **explorativer** Discovery: es gibt eine Protokollfrage,
 * die zurückgibt „das sind meine Datenpunkte". Nur für die ist ein
 * Discovery-Trigger eine sinnvolle Aktion.
 *
 * Bewusst NICHT die Umkehrung von {@link MODBUS_FAMILY}: ein Gerät ohne
 * gesetztes Protokoll oder mit einem Token, das das Frontend noch nicht
 * kennt, ist hier unbekannt und nicht „flach" — die Entscheidung, ob der
 * Client antwortet, fällt dann am Client und nicht in dieser Datei.
 */
export const EXPLORATIVE_PROTOCOLS: readonly string[] = [
  'bacnet-ip',
  'climatix',
  'opc-ua',
  // Siemens OZW672/772: Menübaum über die lokale Web-API.
  'ozw',
];

/**
 * Protokoll-Familie Modbus.
 *
 * Muss mit `ApplySchemaTemplateHandler.ProtocolFamilies` auf dem Server im
 * Gleichschritt bleiben: Modbus-RTU und Modbus-TCP teilen denselben
 * Register-Adressraum (h:/i:/c:/d:), nur der Transport unterscheidet sich.
 * Ein Template für die eine Seite ist damit auf der anderen anwendbar.
 *
 * Das ist eine **Familie und kein Alias**: `DeviceProtocols.Canonical` auf
 * dem Server legt die beiden Tokens gerade NICHT zusammen, und das ist
 * Absicht — ein Gerät weiss, auf welchem Transport es hängt.
 */
export const MODBUS_FAMILY: readonly string[] = ['modbus', 'modbus-tcp', 'modbus-rtu'];

/** Kleinschreibung + trim, damit ein Token aus der Datenbank so vergleichbar
 *  ist wie einer aus einem Formular. */
function normalise(protocol: string | null | undefined): string {
  return (protocol ?? '').trim().toLowerCase();
}

/**
 * True für Modbus-TCP/-RTU. Diese Geräte haben auf Protokollebene **keine
 * Register-Enumeration** — es gibt keine Frage „welche Register hast du".
 * Ein Discovery-Knopf kann dort nichts finden; der Weg zu einem Baum ist
 * ein Register-Template (Konzept J.17).
 */
export function isModbusFamily(protocol: string | null | undefined): boolean {
  const p = normalise(protocol);
  return p.length > 0 && MODBUS_FAMILY.includes(p);
}

/** True wenn zwei Protokoll-Token dasselbe Gerät bedienen können — gleich
 *  oder in derselben Familie. Basis des Template-Filters. */
export function protocolsCompatible(a: string | null | undefined, b: string | null | undefined): boolean {
  const na = normalise(a);
  const nb = normalise(b);
  if (na.length === 0 || nb.length === 0) return false;
  if (na === nb) return true;
  return MODBUS_FAMILY.includes(na) && MODBUS_FAMILY.includes(nb);
}

/** True für Protokolle, für die ein Discovery-Trigger überhaupt Sinn hat. */
export function isExplorativeProtocol(protocol: string | null | undefined): boolean {
  return EXPLORATIVE_PROTOCOLS.includes(normalise(protocol));
}

/**
 * Ein Satz für die UI, warum auf diesem Gerät kein Scan läuft — oder `null`,
 * wenn ein Scan die richtige Aktion ist. Bewusst als Text und nicht als
 * Boolean, weil der Grund am Knopf stehen muss: ein deaktivierter Knopf
 * ohne Begründung ist dieselbe Sackgasse, nur stumm.
 */
export function discoveryUnavailableReason(protocol: string | null | undefined): string | null {
  if (isModbusFamily(protocol)) {
    return 'Modbus hat keine Discovery — es gibt kein Protokoll, das die Register aufzählt. '
      + 'Der Weg zu einem Baum ist ein Register-Template.';
  }
  return null;
}
